import React, { useState, useRef, useEffect } from 'react'

import { AccountSwitcher } from '@barracuda-internal/bds-core-v2'
import { IAccount } from '@barracuda-internal/converged-email-log'
import { Account } from '@barracuda-internal/bds-core-v2/dist/UnifiedComponents/AccountSwitcher/AccountSwitcher'

import AccountSwitcherContainer from 'components/libs/accountSwitcher/accountSwitcherEmailProtection/layout/AccountSwitcherContainer'
import MaskLoader from 'components/libs/contentLoader/MaskLoader'
import { useFormatMessage } from 'lib/localization'
import { useSwitchAccountAsync } from 'components/libs/accountSwitcher/accountSwitcherEmailProtection/hooks/useSwitchAccountAsync'
import { setErrorSnackBar } from 'redux/features/app/appSlice'
import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'

export interface AccountSwitcherEmailProtectionSingleProps {
  accounts: IAccount[]
  selectedAccountId: string
}

type UIStateIdle = { state: 'idle' }
type UIStateSwitching = { state: 'switching'; accountName: string }
type UIState = UIStateIdle | UIStateSwitching

const BASE_I18N_KEY = 'ess.account_switcher'

const AccountSwitcherEmailProtectionSingle = ({
  accounts,
  selectedAccountId
}: AccountSwitcherEmailProtectionSingleProps) => {
  const canSetState = useRef(true)
  const dispatch = useAppDispatch()
  const [uiState, setUiState] = useState<UIState>({ state: 'idle' })
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const switchAccountAsync = useSwitchAccountAsync()
  const authMode = useAppSelector(_store => _store.auth.accessTokenObject?.authMode)

  const onSelectAccountCallback = async (newAccount: Account) => {
    // Login App sessions must use OAuth2 flow for account switching.
    // SOAP /switch-account endpoint is not available for Login App sessions.
    if (authMode === 'login_app') {
      setUiState({ state: 'switching', accountName: newAccount.name })
      window.location.href = `/admin/oauth2/authorize?connection=login_app&account_hint=${newAccount.id}`
      return
    }

    // C1: DEAD CODE - SOAP switch-account fallback
    // Only reached when authMode is undefined (SOAP session) or not 'login_app'
    // Safe to delete when SOAP sessions are fully deprecated
    setUiState({ state: 'switching', accountName: newAccount.name })
    try {
      const res = await switchAccountAsync(newAccount.id)
      if (res.newRegionUrl) {
        window.location.href = res.newRegionUrl
      } else {
        window.location.reload()
      }
    } catch (e) {
      if (!canSetState.current) {
        return
      }
      dispatch(setErrorSnackBar({ message: 'account_switch_failed', params: [newAccount.name] }))
      setUiState({ state: 'idle' })
    }
  }

  useEffect(() => {
    canSetState.current = true
    return () => {
      canSetState.current = false
    }
  }, [])

  switch (uiState.state) {
    case 'switching': {
      return (
        <MaskLoader open fullPage message={`${formatMessage('switching_account', { account: uiState.accountName })}`} />
      )
    }
    case 'idle':
    default: {
      if (accounts.length < 2) {
        return null
      }

      return (
        <AccountSwitcherContainer>
          <AccountSwitcher
            accounts={accounts.map(account => ({
              id: account.bccAccountId,
              name: account.bccAccountName
            }))}
            onSelectAccountCallback={onSelectAccountCallback}
            initAccount={selectedAccountId}
          />
        </AccountSwitcherContainer>
      )
    }
  }
}

export default AccountSwitcherEmailProtectionSingle
